import { CurrencyPipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import type {
  CommerceInsulation,
  TrackedProductComponent,
} from '@analytics/models';
import { componentViewModelSelector } from '@core/experience-editor-utils';
import type {
  InsulationProductType,
  InsulationProductWithCalculations,
} from '@innogy/feh/temporary-core-modules';
import { TextInterpolationService } from '@core/jss-utils';
import { Store } from '@ngrx/store';

import {
  InsulationProductTrackingConfig,
  selectInsulationReceiptViewModel,
} from '../../store';
import { mockInsulationReceiptViewModel } from '../../mocks/mock-insulation-receipt-vm';
import { InsulationReceiptRendering } from '../../models/insulation-receipt.rendering.interface';

@Component({
  selector: 'wl-insulation-receipt[rendering]',
  templateUrl: './insulation-receipt.component.html',
  styleUrls: [
    './insulation-receipt.component.ed.scss',
    './insulation-receipt.component.essent.scss',
  ],
})
export class InsulationReceiptComponent
  implements
    TrackedProductComponent<
      InsulationProductWithCalculations,
      CommerceInsulation
    >
{
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering!: InsulationReceiptRendering;

  trackProductConfig = this.trackingConfig.config;

  viewModel$ = this.store$.select(
    componentViewModelSelector(
      selectInsulationReceiptViewModel,
      mockInsulationReceiptViewModel
    )
  );

  formattedSavings(product: InsulationProductWithCalculations) {
    const formattedSavings =
      this.currencyPipe.transform(
        product.yearlySavings,
        'EUR',
        'symbol',
        '1.0-0'
      ) ?? 0;

    const savingsText = this.interpolationService.interpolate(
      this.rendering.fields.SavingsPerYearText,
      {
        savings: formattedSavings,
      },
      'plain_text'
    );

    return [
      {
        text: savingsText.value,
        inactive: false,
        tooltip: '',
      },
      ...(product.content.usps ?? []).slice(0, 2),
    ];
  }

  formatUnsuitableDisclaimer(
    unsuitableProducts: InsulationProductType[],
    orValue: string
  ) {
    let unsuitableText = '';

    if (unsuitableProducts.length === 1) {
      unsuitableText = unsuitableProducts[0];
    } else {
      unsuitableText = unsuitableProducts.reduce(
        (combined, product, index, array) => {
          if (index === 0) {
            return product;
          } else if (index < array.length - 1 || array.length === 1) {
            return `${combined}, ${product}`;
          } else {
            return `${combined} ${orValue} ${product}`;
          }
        },
        ''
      );
    }

    return this.interpolationService.interpolate(
      this.rendering.fields.ProductSelectionDisclaimer,
      {
        unsuitable: unsuitableText,
      },
      'rich_text'
    );
  }

  constructor(
    private readonly store$: Store,
    private readonly currencyPipe: CurrencyPipe,
    private readonly interpolationService: TextInterpolationService,
    private readonly trackingConfig: InsulationProductTrackingConfig
  ) {}
}
